/**
 * Loads the derived glossary, environment-scoped and cached.
 *
 * Kept out of ExplorerStore because the glossary is consumed in three places
 * that do not share a store: the standalone glossary page, the query builder's
 * pickers, and the results grid headers. A hook over the existing LocalForage
 * cache is the smaller dependency.
 *
 * Stale-while-revalidate, matching how filters and metrics already load: the
 * cached copy renders immediately and a fresh copy replaces it when it arrives.
 * The payload is ~590 KB uncompressed and changes only on deploy, so showing a
 * stale copy for one paint is the right trade against blocking on the network.
 */
import { useEffect, useMemo, useState } from "react";
import { useLeagueApi, useLeagueEnvironment } from "../data/LeagueEnvironmentProvider";
import { CacheKey, readFromCache, writeToCache } from "../data/Cache";
import { GlossaryEntry, GlossaryResponse } from "../models/Glossary";

/**
 * Shared per environment, at module scope, because the query builder renders one
 * DimensionSelect per filter row and each would otherwise issue its own request
 * for the same ~590 KB payload. Resolved values are kept too, so a component
 * mounting later renders from memory rather than re-reading LocalForage.
 */
const loaded = new Map<string, GlossaryResponse>();
const inFlight = new Map<string, Promise<GlossaryResponse>>();

interface UseGlossaryResult {
  glossary: GlossaryResponse | null;
  /** Filters and metrics in one list, for search and for name lookups. */
  entries: GlossaryEntry[];
  byName: Map<string, GlossaryEntry>;
  loading: boolean;
  error: string | null;
}

export function useGlossary(): UseGlossaryResult {
  const api = useLeagueApi();
  const environment = useLeagueEnvironment();
  const [glossary, setGlossary] = useState<GlossaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const already = loaded.get(environment);
    if (already) {
      setGlossary(already);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    readFromCache(CacheKey.Glossary, environment).then((cached) => {
      // Only paint the cached copy if the network has not already won the race.
      if (!cancelled && cached && !loaded.has(environment)) {
        setGlossary(cached as GlossaryResponse);
        setLoading(false);
      }
    });

    let request = inFlight.get(environment);
    if (!request) {
      request = api.getGlossary().then(({ data }) => {
        loaded.set(environment, data);
        inFlight.delete(environment);
        writeToCache(CacheKey.Glossary, data, environment);
        return data;
      });
      inFlight.set(environment, request);
      request.catch(() => inFlight.delete(environment));
    }

    request
      .then((data) => {
        if (!cancelled) setGlossary(data);
      })
      .catch((e) => {
        // A cached copy is still useful, so only surface the failure without one.
        if (!cancelled) setError(e?.message ?? "Could not load the glossary");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, environment]);

  const { entries, byName } = useMemo(() => {
    const all: GlossaryEntry[] = glossary
      ? [...glossary.filters, ...glossary.metrics]
      : [];
    // No name is currently both a filter and a metric, but nothing enforces
    // that, so the collision is resolved deliberately: metrics go in first and
    // filters overwrite, matching how the query builder resolves a name typed
    // into the filter box.
    const map = new Map<string, GlossaryEntry>();
    glossary?.metrics.forEach((m) => map.set(m.name, m));
    glossary?.filters.forEach((f) => map.set(f.name, f));
    return { entries: all, byName: map };
  }, [glossary]);

  return { glossary, entries, byName, loading, error };
}
